import { useMutation, useQuery } from '@apollo/client';
import { faArrowDownToLine, faArrowUpFromLine } from '@fortawesome/pro-regular-svg-icons';
import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ConfigJsonPanel from './ConfigJsonPanel.tsx';
import FileNameSection from './FileNameSection.tsx';
import MappingTestControls from './MappingTestControls.tsx';
import OutputMapperPanel from './OutputMapperPanel.tsx';
import {
    OutputSettingsSelector,
    getIterationType,
    getOutputType,
    getXmlSerializerModule,
    iterationEditorConfigTransform,
    iterationMergeVisualEditorChange,
    iterationVisualEditorKey,
    outputTypeOptions,
    renderTabularMappedOutput,
    usesIteration,
} from './OutputSettings.tsx';
import ProductionOutputNotice from './ProductionOutputNotice.tsx';
import {
    GET_OUTPUT_MAPPER_CONFIG,
    GET_OUTPUT_MAPPER_TEST_RECORDS,
    TEST_OUTPUT_MAPPING,
    UPDATE_OUTPUT_MAPPER_CONFIG,
} from './queries.ts';
import TemplatePanel from './TemplatePanel.tsx';
import type { OutputMapperTestingConfig, RecordType, TestRecord, TestResult } from './types.ts';
import Button from '../../../core/components/Button.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import SegmentedButton from '../../../core/components/SegmentedButton.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import Tooltip from '../../../core/components/Tooltip.tsx';
import type { OutputMapperConfig } from '../OutputMapperEditor/types.ts';

type SubTab = 'configuration' | 'visualEditor' | 'template' | 'configJson';

type ConfigUpdater = OutputMapperConfig | ((current: OutputMapperConfig | null) => OutputMapperConfig | null);

export interface OutputMapperEditorPanelProps {
    /** Registry key identifying the owner slot, e.g. "list_of_services", "orderwise.order", "netsuite.invoice". */
    outputMapperKey: string;
    /** Owner id (channel id for channels, integration id for integrations). Config can't load/save without it. */
    ownerId?: string;
    /** Stable value handed to the inner panels' `recordType`/`configType` props (display/reset bookkeeping only). */
    recordType?: RecordType;
    /** Override the mapped-output render. Defaults to a table for list results, JSON tree otherwise. */
    renderMappedOutput?: (mappedOutput: any, config: OutputMapperConfig | null) => React.ReactNode;
    /** Extra slot rendered above the visual editor, below the shared settings selector. */
    renderAboveEditor?: (ctx: {
        config: OutputMapperConfig | null;
        updateConfig: (next: ConfigUpdater) => void;
    }) => React.ReactNode;
    /** Override the editor display transform. Defaults to surfacing per-iteration roots for article suggestions. */
    editorConfigTransform?: (config: OutputMapperConfig) => OutputMapperConfig;
    /** Override how a visual-editor change folds back into stored config. Defaults to preserving input_types. */
    mergeVisualEditorChange?: (next: OutputMapperConfig, current: OutputMapperConfig | null) => OutputMapperConfig;
    /** Override the visual-editor remount key. Defaults to the iteration type. */
    getVisualEditorKey?: (config: OutputMapperConfig | null) => string | undefined;
    /** Hide the Configuration sub-tab for owners whose output type and iteration are fixed (e.g. a JSON API push). */
    hideConfiguration?: boolean;
}

const ExportIcon = withIcon(faArrowDownToLine);
const ImportIcon = withIcon(faArrowUpFromLine);

// Owners whose Output Studio switch is a channel setting (the others are ERP integrations).
const CHANNEL_OWNER_KEYS = ['order', 'list_of_services'];

// Catalogs the backend injects on every read (and re-injects after an import); not part of an exported config.
const EDITOR_CATALOG_KEYS = ['template_defaults', 'template_xsds', 'xml_serializer_modes', 'target_field_options'];

const withoutEditorCatalogs = (config: OutputMapperConfig) =>
    Object.fromEntries(Object.entries(config).filter(([key]) => !EDITOR_CATALOG_KEYS.includes(key)));

const Chip = ({ children }: { children: React.ReactNode }) => (
    <span className="rounded border border-line-weak bg-surface-medium px-2 py-1 text-xs font-medium text-content-strong">
        {children}
    </span>
);

/**
 * Read-only summary of the saved output settings, shown above the visual editor with a shortcut back to
 * the Configuration tab where they are edited.
 */
const ConfigSummaryChips = ({
    config,
    onEditConfiguration,
}: {
    config: OutputMapperConfig | null;
    onEditConfiguration: () => void;
}) => {
    const { t } = useTranslation('integrations');
    if (!config) return null;

    const outputType = getOutputType(config);
    const iterationType = getIterationType(config);
    const outputLabels = Object.fromEntries(outputTypeOptions(t).map((option) => [option.value, option.label]));
    const iterationLabels: Record<string, string> = {
        record: t('outputMapper.settings.iterationType.record', 'Record'),
        line_items: t('outputMapper.settings.iterationType.lineItems', 'Line items'),
        article_suggestions: t('outputMapper.settings.iterationType.articleSuggestions', 'Article suggestions'),
    };
    const serializerLabel =
        outputType === 'xml'
            ? (config.xml_serializer_modes ?? []).find((mode) => mode.value === getXmlSerializerModule(config))?.label
            : undefined;

    return (
        <div className="flex flex-wrap items-center gap-2 text-content-weak">
            <Chip>{outputLabels[outputType] ?? outputType}</Chip>
            {usesIteration(outputType) && <Chip>{iterationLabels[iterationType] ?? iterationType}</Chip>}
            {serializerLabel && <Chip>{serializerLabel}</Chip>}
            <button
                type="button"
                onClick={onEditConfiguration}
                className="ml-1 text-xs font-medium text-content-brand hover:underline enabled:cursor-pointer"
            >
                {t('outputMapper.editConfiguration', 'Edit configuration →')}
            </button>
        </div>
    );
};

/**
 * Self-contained output-mapper editor for any registered owner. Owns its config state and drives the
 * registry-keyed generic API (read / save / schema-refresh / test-records / test-mapping). Used directly
 * by every owner; the List of Services panel wraps it to add its document/article output-level controls.
 */
const OutputMapperEditorPanel = ({
    outputMapperKey,
    ownerId,
    recordType = 'order',
    renderMappedOutput,
    renderAboveEditor,
    editorConfigTransform,
    mergeVisualEditorChange,
    getVisualEditorKey,
    hideConfiguration = false,
}: OutputMapperEditorPanelProps) => {
    const { t } = useTranslation('integrations');

    const effectiveEditorConfigTransform = editorConfigTransform ?? iterationEditorConfigTransform;
    const effectiveMergeVisualEditorChange = mergeVisualEditorChange ?? iterationMergeVisualEditorChange;
    const effectiveGetVisualEditorKey = getVisualEditorKey ?? iterationVisualEditorKey;
    const effectiveRenderMappedOutput = renderMappedOutput ?? renderTabularMappedOutput;

    const { data, loading, refetch } = useQuery(GET_OUTPUT_MAPPER_CONFIG, {
        variables: { key: outputMapperKey, ownerId },
        fetchPolicy: 'network-only',
        skip: !ownerId,
    });

    const [config, setConfig] = useState<OutputMapperConfig | null>(null);
    const [warnings, setWarnings] = useState<string[]>([]);
    const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
    const [saveError, setSaveError] = useState<string | null>(null);
    const defaultSubTab: SubTab = hideConfiguration ? 'visualEditor' : 'configuration';
    const [activeSubTab, setActiveSubTab] = useState<SubTab>(defaultSubTab);
    // A target field picked in the template editor: the Target fields tab opens with it selected.
    const [focusTarget, setFocusTarget] = useState<string | null>(null);
    // Bumped after a server-driven config refresh (e.g. Sync configured fields) to remount the visual
    // editor. The editor provider seeds its state from initialConfig only on mount, so without this the
    // Target Fields list would keep showing the pre-sync mapping.
    const [editorRefreshNonce, setEditorRefreshNonce] = useState(0);

    useEffect(() => {
        const payload = data?.outputMapperConfig;
        if (payload) {
            setConfig(payload.config);
            setWarnings(payload.warnings ?? []);
        }
    }, [data]);

    const [updateConfig] = useMutation(UPDATE_OUTPUT_MAPPER_CONFIG);

    const updateConfigState = (next: ConfigUpdater) => {
        setConfig((current) => (typeof next === 'function' ? next(current) : next));
        setSaveState('idle');
    };

    const editorConfig = useMemo(
        () => (config ? effectiveEditorConfigTransform(config) : config),
        [config, effectiveEditorConfigTransform]
    );

    const handleVisualEditorChange = (next: OutputMapperConfig) => {
        setConfig((current) => effectiveMergeVisualEditorChange(next, current));
        setSaveState('idle');
    };

    const importInputRef = useRef<HTMLInputElement>(null);
    // channels deliver a file (file name, line ending, encoding); ERP integrations push a payload to an API
    const isChannelOwner = CHANNEL_OWNER_KEYS.includes(outputMapperKey);
    const { publishToast } = useToaster();

    const handleExport = () => {
        if (!config) return;
        const exported = withoutEditorCatalogs(config);
        const blob = new Blob([JSON.stringify(exported, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `output-studio-${(config.name || outputMapperKey).replace(/[^\w.-]+/g, '_')}.json`;
        link.click();
        URL.revokeObjectURL(url);
    };

    const handleImport = async (file: File | undefined) => {
        if (!file) return;
        try {
            const imported = JSON.parse(await file.text());
            if (!imported || typeof imported !== 'object' || !Array.isArray(imported.output_mapping)) {
                throw new Error('not an Output Studio configuration');
            }
            // keep the editor catalogs of this owner; everything else comes from the file
            setConfig((current) => ({
                ...imported,
                ...Object.fromEntries(EDITOR_CATALOG_KEYS.map((key) => [key, current?.[key]])),
            }));
            setSaveState('idle');
            setEditorRefreshNonce((nonce) => nonce + 1);
            publishToast({
                description: t('outputMapper.imported', 'Configuration imported. Review it and save to keep it.'),
                status: 'success',
            });
        } catch (error) {
            publishToast({
                description: t('outputMapper.importFailed', {
                    defaultValue: 'The file could not be imported: {{error}}',
                    error: error instanceof Error ? error.message : String(error),
                }),
                status: 'error',
            });
        }
    };

    const handleSave = async () => {
        if (!ownerId || !config) return;
        setSaveState('saving');
        setSaveError(null);
        try {
            const result = await updateConfig({
                variables: { key: outputMapperKey, ownerId, config: withoutEditorCatalogs(config) },
            });
            const payload = result.data?.updateOutputMapperConfig;
            if (payload?.success) {
                setWarnings(payload.warnings ?? []);
                setSaveState('saved');
                await refetch();
            } else {
                setSaveState('error');
                setSaveError((payload?.errors ?? []).join('\n') || 'Unknown error');
            }
        } catch (e: any) {
            setSaveState('error');
            setSaveError(e?.message ?? 'Unknown error');
        }
    };

    // Server-detected divergence between the stored config and the current backend state (read-time
    // dry-runs of the two reconcile operations). Derived from the query so a refetch clears the notice.
    const schemaOutdated: boolean = data?.outputMapperConfig?.schemaOutdated ?? false;
    const configuredFieldsOutOfSync: boolean = data?.outputMapperConfig?.configuredFieldsOutOfSync ?? false;

    const handleUpdateMapping = async () => {
        if (!ownerId) return;
        setSaveState('saving');
        setSaveError(null);
        try {
            // Configured-field sync already includes the schema refresh, so one call covers both kinds
            // of divergence; refresh-only owners (no field sync) fall back to the plain schema refresh.
            const variables = configuredFieldsOutOfSync
                ? { key: outputMapperKey, ownerId, syncConfiguredFields: true }
                : { key: outputMapperKey, ownerId, refreshSchema: true };
            const result = await updateConfig({ variables });
            const payload = result.data?.updateOutputMapperConfig;
            if (payload?.success) {
                setSaveState('saved');
                // Seed config from the refetch result and remount the visual editor in the same render so
                // the Target Fields list reflects the newly synced mapping instead of the stale one.
                const refreshed = await refetch();
                const refreshedPayload = refreshed.data?.outputMapperConfig;
                if (refreshedPayload) {
                    setConfig(refreshedPayload.config);
                    setWarnings(refreshedPayload.warnings ?? []);
                }
                setEditorRefreshNonce((n) => n + 1);
            } else {
                setSaveState('error');
                setSaveError((payload?.errors ?? []).join('\n') || 'Unknown error');
            }
        } catch (e: any) {
            setSaveState('error');
            setSaveError(e?.message ?? 'Unknown error');
        }
    };

    const testingConfig: OutputMapperTestingConfig = useMemo(
        () => ({
            fetchTestRecordsQuery: GET_OUTPUT_MAPPER_TEST_RECORDS,
            testMappingMutation: TEST_OUTPUT_MAPPING,
            buildFetchVariables: (id, limit) => ({ key: outputMapperKey, ownerId: id, limit }),
            buildTestVariables: (recordId, _recordType, liveConfig) => ({
                recordId,
                key: outputMapperKey,
                config: liveConfig,
                // The output settings selector drives the render format; XML renders via xml_serializer_module.
                returnType: liveConfig?.output_type ?? 'json',
            }),
            getRecordsFromData: (testData): TestRecord[] => testData?.outputMapperTestRecords ?? [],
            getTestResultFromData: (testData): TestResult => testData?.testOutputMapping?.result ?? { success: false },
        }),
        [outputMapperKey]
    );

    // The Template tab only exists for the template output type; leave it gracefully when the
    // output type is switched away while it is active.
    const isTemplateOutput = getOutputType(config) === 'template';
    useEffect(() => {
        if (activeSubTab === 'template' && config && !isTemplateOutput) {
            setActiveSubTab(defaultSubTab);
        }
    }, [activeSubTab, config, isTemplateOutput, defaultSubTab]);

    const allSubTabs: { value: SubTab; label: string; hint: string }[] = [
        {
            value: 'configuration',
            label: t('outputMapper.tabs.configuration', 'Configuration'),
            hint: t(
                'outputMapper.tabs.configurationHint',
                'Output type, file format, encoding and file name of this output.'
            ),
        },
        ...(isTemplateOutput
            ? [
                  {
                      value: 'template' as const,
                      label: t('outputMapper.tabs.template', 'Template'),
                      hint: t(
                          'outputMapper.tabs.templateHint',
                          'The Jinja template that writes the file, with Create/Edit with AI.'
                      ),
                  },
              ]
            : []),
        {
            value: 'visualEditor',
            label: t('outputMapper.tabs.visualEditor', 'Target fields'),
            hint: t(
                'outputMapper.tabs.visualEditorHint',
                'The values the output is built from: where each target field reads its data and how it is transformed.'
            ),
        },
        {
            value: 'configJson',
            label: t('outputMapper.tabs.configJson', 'Config JSON'),
            hint: t(
                'outputMapper.tabs.configJsonHint',
                'The complete configuration as JSON, for review or copying to another channel.'
            ),
        },
    ];

    const subTabs = hideConfiguration ? allSubTabs.filter((tab) => tab.value !== 'configuration') : allSubTabs;

    if (loading && !config) {
        return <div className="p-4 text-content-weak">{t('outputMapper.loading', 'Loading…')}</div>;
    }

    const visualEditorKey = effectiveGetVisualEditorKey(config);

    const updateNotice =
        schemaOutdated || configuredFieldsOutOfSync ? (
            <div className="flex items-start justify-between gap-3 rounded border border-blue-300 bg-blue-50 p-3 text-sm text-blue-900">
                <p>
                    {t(
                        'outputMapper.updateNotice.message',
                        'It’s no longer matching the channel configuration. Update the mapping to have it matching.'
                    )}
                </p>
                <button
                    type="button"
                    onClick={handleUpdateMapping}
                    disabled={saveState === 'saving' || !ownerId}
                    className="flex-none px-3 py-1.5 text-sm font-medium rounded border border-blue-300 bg-white text-blue-900 hover:enabled:bg-blue-100 disabled:opacity-50 enabled:cursor-pointer"
                >
                    {saveState === 'saving'
                        ? t('outputMapper.updating', 'Updating…')
                        : t('outputMapper.updateMapping', 'Update mapping')}
                </button>
            </div>
        ) : null;

    return (
        <div className="flex flex-col flex-1 min-h-0 gap-3 p-4">
            {warnings.length > 0 && (
                <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                    <div className="font-medium mb-1">{t('outputMapper.warningsTitle', 'Mapping warnings')}</div>
                    <ul className="list-disc list-inside">
                        {warnings.map((w, i) => (
                            <li key={i}>{w}</li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="flex-none flex items-center gap-3">
                {/* each tab sits in a span: a tooltip trigger sets its own data-state, which would hide the checked style */}
                <SegmentedButton
                    value={activeSubTab}
                    onValueChange={(next) => {
                        if (next === 'visualEditor') setFocusTarget(null);
                        setActiveSubTab(next as SubTab);
                    }}
                >
                    {subTabs.map((tab) => (
                        <Tooltip key={tab.value} content={tab.hint} side="bottom">
                            <span className="inline-flex">
                                <SegmentedButton.Item value={tab.value}>{tab.label}</SegmentedButton.Item>
                            </span>
                        </Tooltip>
                    ))}
                </SegmentedButton>
                <div className="ml-auto flex items-center gap-2">
                    <input
                        ref={importInputRef}
                        type="file"
                        accept=".json,application/json"
                        className="hidden"
                        onChange={(event) => {
                            handleImport(event.target.files?.[0]);
                            event.target.value = '';
                        }}
                    />
                    <Tooltip
                        content={t(
                            'outputMapper.exportHint',
                            'Download this configuration as a JSON file, e.g. to import it into another channel.'
                        )}
                    >
                        <Button
                            type="button"
                            variant="outline"
                            className="flex items-center gap-2"
                            disabled={!config}
                            onClick={handleExport}
                        >
                            <ExportIcon />
                            {t('outputMapper.export', 'Export')}
                        </Button>
                    </Tooltip>
                    <Tooltip
                        content={t(
                            'outputMapper.importHint',
                            'Load a configuration from a JSON file. Nothing is stored until you save.'
                        )}
                    >
                        <Button
                            type="button"
                            variant="outline"
                            className="flex items-center gap-2"
                            disabled={!config}
                            onClick={() => importInputRef.current?.click()}
                        >
                            <ImportIcon />
                            {t('outputMapper.import', 'Import')}
                        </Button>
                    </Tooltip>
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={saveState === 'saving' || !config}
                        className="px-3 py-1.5 text-sm font-medium rounded bg-surface-brand-strong-default text-content-white dark:text-content-black hover:enabled:bg-surface-brand-strong-hover active:enabled:bg-surface-brand-strong-active disabled:opacity-50 enabled:cursor-pointer"
                    >
                        {saveState === 'saving'
                            ? t('outputMapper.saving', 'Saving…')
                            : t('outputMapper.save', 'Save configuration')}
                    </button>
                    {saveState === 'saved' && (
                        <span className="text-sm text-emerald-700">{t('outputMapper.saved', 'Saved')}</span>
                    )}
                    {saveState === 'error' && saveError && <span className="text-sm text-red-700">{saveError}</span>}
                </div>
            </div>

            <div className="flex-1 min-h-0 overflow-hidden">
                {activeSubTab === 'configuration' && (
                    <div className="h-full min-h-0 overflow-auto">
                        <div className="flex max-w-3xl flex-col gap-6 pb-2">
                            <div className="flex flex-col gap-3">
                                <ProductionOutputNotice
                                    isActive={data?.outputMapperConfig?.isActive}
                                    ownerKind={isChannelOwner ? 'channel' : 'integration'}
                                />
                                <OutputSettingsSelector
                                    config={config}
                                    updateConfig={updateConfigState}
                                    belowMappingSource={updateNotice}
                                    isFileOutput={isChannelOwner}
                                />
                            </div>
                            {config && isChannelOwner && (
                                <FileNameSection
                                    config={config}
                                    onConfigChange={updateConfigState}
                                    ownerId={ownerId}
                                    recordType={recordType}
                                    testingConfig={testingConfig}
                                />
                            )}
                        </div>
                    </div>
                )}
                {activeSubTab === 'visualEditor' && (
                    <div className="flex h-full min-h-0 flex-col gap-3">
                        {hideConfiguration && updateNotice}
                        <div className="flex flex-none flex-wrap items-center justify-between gap-3">
                            {!hideConfiguration && (
                                <ConfigSummaryChips
                                    config={config}
                                    onEditConfiguration={() => setActiveSubTab('configuration')}
                                />
                            )}
                            <div className="ml-auto">
                                <MappingTestControls
                                    integrationId={ownerId}
                                    recordType={recordType}
                                    config={config}
                                    testingConfig={testingConfig}
                                    renderMappedOutput={effectiveRenderMappedOutput}
                                />
                            </div>
                        </div>
                        {renderAboveEditor?.({ config, updateConfig: updateConfigState })}
                        <div className="min-h-0 flex-1">
                            <OutputMapperPanel
                                key={`${visualEditorKey ?? 'default'}:${editorRefreshNonce}:${focusTarget ?? ''}`}
                                config={editorConfig}
                                onConfigChange={handleVisualEditorChange}
                                initialTarget={focusTarget}
                            />
                        </div>
                    </div>
                )}
                {activeSubTab === 'template' && (
                    <div className="flex h-full min-h-0 flex-col gap-3">
                        <div className="flex flex-none flex-wrap items-center justify-between gap-3">
                            <ConfigSummaryChips
                                config={config}
                                onEditConfiguration={() => setActiveSubTab('configuration')}
                            />
                            <MappingTestControls
                                integrationId={ownerId}
                                recordType={recordType}
                                config={config}
                                testingConfig={testingConfig}
                                renderMappedOutput={effectiveRenderMappedOutput}
                            />
                        </div>
                        <div className="min-h-0 flex-1">
                            <TemplatePanel
                                config={config}
                                onConfigChange={updateConfigState}
                                onOpenTargetField={(target) => {
                                    setFocusTarget(target);
                                    setActiveSubTab('visualEditor');
                                }}
                            />
                        </div>
                    </div>
                )}
                {activeSubTab === 'configJson' && (
                    <ConfigJsonPanel
                        recordType={recordType}
                        config={config}
                        onConfigChange={(next) => {
                            setConfig(next);
                            setSaveState('idle');
                        }}
                    />
                )}
            </div>
        </div>
    );
};

export default OutputMapperEditorPanel;
